/**
 *  Copyright (C) 2022 - Jeci SARL - https://jeci.fr
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU Affero General Public License as
 *  published by the Free Software Foundation, either version 3 of the
 *  License, or (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU Affero General Public License for more details.
 *
 *  You should have received a copy of the GNU Affero General Public License
 *  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import { typesApi, aspectsApi } from "../AlfrescoApi.js";

class AlfrescoModelService {
  /**
   * Get all aspects from Alfresco
   * @param {object} params Query parameters (where, skipCount, maxItems, include)
   * @returns {Promise<any>}
   */
  getAspects(params = {}) {
    return aspectsApi.listAspects(params);
  }

  /**
   * Get a specific aspect by ID
   * @param {string} aspectId The aspect identifier
   * @returns {Promise<any>}
   */
  getAspect(aspectId) {
    return aspectsApi.getAspect(aspectId);
  }

  /**
   * Get all types from Alfresco
   * @param {object} params Query parameters (where, skipCount, maxItems, include)
   * @returns {Promise<any>}
   */
  getTypes(params = {}) {
    return typesApi.listTypes(params);
  }

  /**
   * Get a specific type by ID
   * @param {string} typeId The type identifier
   * @returns {Promise<any>}
   */
  getType(typeId) {
    return typesApi.getType(typeId);
  }

  /**
   * Extract properties from a type definition
   * @param {object} typeEntry The type entry from Alfresco API
   * @returns {Array<{key: string, title: string, dataType: string}>}
   */
  extractPropertiesFromType(typeEntry) {
    const properties = [];

    if (typeEntry.properties) {
      Object.entries(typeEntry.properties).forEach(([key, prop]) => {
        properties.push({
          key: prop.id,
          title: prop.title || key,
          dataType: prop.dataType || "d:text",
          mandatory: prop.mandatory || false,
        });
      });
    }

    return properties;
  }

  /**
   * Extract properties from an aspect definition
   * @param {object} aspectEntry The aspect entry from Alfresco API
   * @returns {Array<{key: string, title: string, dataType: string}>}
   */
  extractPropertiesFromAspect(aspectEntry) {
    const properties = [];

    if (aspectEntry.properties) {
      Object.entries(aspectEntry.properties).forEach(([key, prop]) => {
        properties.push({
          key: prop.id,
          title: prop.title || key,
          dataType: prop.dataType || "d:text",
          mandatory: prop.mandatory || false,
        });
      });
    }

    return properties;
  }

  /**
   * Get properties for a given aspect in normalized format
   * @param {string} aspectId
   * @returns {Promise<Array<{id: string, title: string, dataType: string, isMandatory: boolean, isMultiValued: boolean}>>}
   */
  async getPropertiesForAspect(aspectId) {
    const response = await this.getAspect(aspectId);
    const entry = response.entry || response;
    if (!entry.properties) {
      return [];
    }
    const parentKeys = await this._getParentPropertyKeys(
      entry.parentId,
      "aspect",
    );
    return this._normalizeProperties(entry.properties, parentKeys);
  }

  /**
   * Get properties for a given type in normalized format
   * @param {string} typeId
   * @returns {Promise<Array<{id: string, title: string, dataType: string, isMandatory: boolean, isMultiValued: boolean}>>}
   */
  async getPropertiesForType(typeId) {
    const response = await this.getType(typeId);
    const entry = response.entry || response;
    if (!entry.properties) {
      return [];
    }
    const parentKeys = await this._getParentPropertyKeys(
      entry.parentId,
      "type",
    );
    return this._normalizeProperties(entry.properties, parentKeys);
  }

  _normalizeProperties(properties, excludeKeys = new Set()) {
    return Object.entries(properties)
      .filter(([key, prop]) => !excludeKeys.has(prop.id || key))
      .map(([key, prop]) => ({
        id: prop.id || key,
        title: prop.title || key,
        dataType: prop.dataType || "d:text",
        isMandatory: prop.isMandatory || prop.mandatory || false,
        isMultiValued: prop.isMultiValued || prop.multiValued || false,
      }));
  }

  async _getParentPropertyKeys(parentId, kind) {
    if (!parentId) {
      return new Set();
    }
    try {
      const response =
        kind === "aspect"
          ? await this.getAspect(parentId)
          : await this.getType(parentId);
      const entry = response.entry || response;
      if (!entry.properties) {
        return new Set();
      }
      return new Set(
        Object.entries(entry.properties).map(([key, prop]) => prop.id || key),
      );
    } catch {
      return new Set();
    }
  }

  /**
   * Get all available property keys from Alfresco types and aspects
   * @returns {Promise<Array<string>>}
   */
  async getAllPropertyKeys() {
    const propertyKeys = new Set();

    try {
      // Fetch all types
      const typesResponse = await this.getTypes({ maxItems: 1000 });
      if (typesResponse.list?.entries) {
        for (const entry of typesResponse.list.entries) {
          const typeDetails = await this.getType(entry.entry.id);
          const properties = this.extractPropertiesFromType(typeDetails.entry);
          properties.forEach((prop) => propertyKeys.add(prop.key));
        }
      }

      // Fetch all aspects
      const aspectsResponse = await this.getAspects({ maxItems: 1000 });
      if (aspectsResponse.list?.entries) {
        for (const entry of aspectsResponse.list.entries) {
          const aspectDetails = await this.getAspect(entry.entry.id);
          const properties = this.extractPropertiesFromAspect(
            aspectDetails.entry,
          );
          properties.forEach((prop) => propertyKeys.add(prop.key));
        }
      }
    } catch (error) {
      console.error("Failed to fetch property keys from Alfresco", error);
    }

    return Array.from(propertyKeys).sort();
  }
}

export default new AlfrescoModelService();
